import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Checkbox,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Theme,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import React, { RefObject, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableInstance } from 'react-table';

import { ReactComponent as ColumnsIcon } from '@work4all/assets/icons/columns.svg';
import { ReactComponent as DuplicateIcon } from '@work4all/assets/icons/duplicate.svg';
import { ReactComponent as AddIcon } from '@work4all/assets/icons/outline-add-24.svg';

import { SplitButton } from '@work4all/components/lib/components/split-button/SplitButton';
import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';

import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';

import { ChecklistPositionKind } from '@work4all/models/lib/Enums/ChecklistPositionKind.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { ColumnsVisibility } from '../../../../../../../../../../components/data-tables/table/components/toolbar/components/columns-visibility';
import { settings, useSetting } from '../../../../../../../../../../settings';
import {
  DEFAULT_VISIBLE_CRM_COLUMNS,
  RowSizeMode,
} from '../../../../../../../../../../settings/settings';
import { EditStateContext } from '../../../../../../erp/components/tab-panels/positions/components/edit-table/hooks/use-editable-state';
import { IEditTable } from '../../../../../../erp/components/tab-panels/positions/components/edit-table/types';
import { useResetColumns } from '../../../../../../erp/components/tab-panels/positions/components/hooks/use-reset-columns';
import {
  RowSizePicker,
  RowSizePickerPopover,
} from '../../../../../../erp/components/tab-panels/positions/components/row-size-picker/RowSizePicker';

import { CheckListPositionsTableHeaderColumnsVisibility } from './CheckListPositionsTableHeaderColumnsVisibility';

type Props = {
  onAdd: (context: EditStateContext) => void;
  onDelete: () => void;
  onClone: () => void;
  deleteDisabled: boolean;
  cloneDisabled: boolean;
  tableInstanceRef: RefObject<TableInstance> | undefined;
  editTableRef?: RefObject<IEditTable>;
  rowSize: RowSizeMode;
  onRowSizeChange: (value: RowSizeMode) => void;
  automaticNumbering: boolean;
  onAutomaticNumberingChange: (value: boolean) => void;
};

export const CheckListPositionsTableHeader = ({
  onAdd,
  onClone,
  onDelete,
  deleteDisabled,
  cloneDisabled,
  tableInstanceRef,
  editTableRef,
  rowSize,
  onRowSizeChange,
  automaticNumbering,
  onAutomaticNumberingChange,
}: Props) => {
  const { t } = useTranslation();
  const [rowSizePickerOpen, setRowSizePickerOpen] = useState(false);

  // Helper to ensure edits are committed before adding a new position
  const safeAdd = useCallback(
    (context: EditStateContext) => {
      if (
        editTableRef?.current &&
        typeof editTableRef.current.scrollTo === 'function'
      ) {
        editTableRef.current.scrollTo();
      }
      onAdd(context);
    },
    [editTableRef, onAdd]
  );

  const splitButtonOptions = useMemo(() => {
    return [
      {
        id: 'add-row',
        name: t('CHECK_LIST_POSITIONS_TABLE.LINE'),
        onClick: () => safeAdd({ kind: ChecklistPositionKind.DEFAULT }),
        icon: <AddIcon />,
      },
      {
        id: 'add-default-row',
        name: t('CHECK_LIST_POSITIONS_TABLE.NEW_LINE'),
        onClick: () => safeAdd({ kind: ChecklistPositionKind.DEFAULT }),
      },
      {
        id: 'add-title-row',
        name: t('CHECK_LIST_POSITIONS_TABLE.NEW_TITLE_LINE'),
        onClick: () => safeAdd({ kind: ChecklistPositionKind.HEADLINE }),
      },
      {
        id: 'add-empty-row',
        name: t('CHECK_LIST_POSITIONS_TABLE.NEW_EMPTY_LINE'),
        onClick: () => safeAdd({ kind: ChecklistPositionKind.EMPTY_ROW }),
      },
    ];
  }, [safeAdd, t]);

  const { handleClick, ...menuState } = usePopoverState('bottom');
  const { onClose: closeMenu } = menuState;

  const withAutoClose =
    (handler: React.MouseEventHandler<HTMLElement>) =>
    (event: React.MouseEvent<HTMLElement>) => {
      handler(event);
      closeMenu();
    };

  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm')
  );

  // Row size picker logic
  const SIZES: RowSizeMode[] = ['SINGLE', 'AUTO'];
  const handleRowSizeSelect = (size: RowSizeMode) => {
    setRowSizePickerOpen(false);
    onRowSizeChange(size);
    closeMenu();
  };

  // Columns visibility logic
  const columnsPopoverState = usePopoverState();
  const columnSettings = useSetting(
    settings.crmPositionsConfig(Entities.checkList)
  );
  const resetColumns = useResetColumns({
    columnSettings,
    tableInstanceRef,
    defaultColumns: DEFAULT_VISIBLE_CRM_COLUMNS,
  });

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      paddingLeft="12px"
      alignItems="center"
    >
      <Box display="flex">
        <SplitButton
          firstIsTitle
          hideSelection
          hideGroupLabels
          variant="outlined"
          options={splitButtonOptions}
          onClick={(option) => {
            option.onClick();
          }}
          renderItem={(option) => {
            return (
              <Box display="flex" gap="0.75rem" alignItems="center">
                {option.icon}
                {option.name}
              </Box>
            );
          }}
        />

        <IconButton onClick={onClone} disabled={cloneDisabled}>
          <DuplicateIcon />
        </IconButton>

        <IconButton onClick={onDelete} disabled={deleteDisabled}>
          <DeleteIcon color="primary" />
        </IconButton>
      </Box>

      {isMobile ? (
        <Box display="flex" alignItems="center">
          <Tooltip title={t('MASK.OPTIONS')} placement="top">
            <IconButton size="medium" color="primary" onClick={handleClick}>
              <MoreVertIcon />
            </IconButton>
          </Tooltip>

          <Menu {...menuState}>
            {/* Automatic Numbering - Checkbox MenuItem */}
            <MenuItem
              onClick={withAutoClose((e) => {
                e.stopPropagation();
                onAutomaticNumberingChange(!automaticNumbering);
              })}
            >
              <ListItemIcon style={{ minWidth: '40px' }}>
                <Checkbox
                  checked={automaticNumbering}
                  onChange={(e) => e.preventDefault()}
                  onClick={(e) => e.stopPropagation()}
                />
              </ListItemIcon>
              <ListItemText>
                {t('CHECK_LIST_POSITIONS_TABLE.AUTOMATIC_NUMBERS')}
              </ListItemText>
            </MenuItem>

            {/* Row Size Picker */}
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                setRowSizePickerOpen(!rowSizePickerOpen);
                closeMenu();
              }}
            >
              <ListItemText
                secondary={t(`POSITIONS.ROW_SIZE.TOOLTIP`)}
                style={{ textAlign: 'left' }}
              />
              <ListItemText
                secondary={t(`POSITIONS.ROW_SIZE.${rowSize}`)}
                style={{ textAlign: 'right' }}
              />
            </MenuItem>

            {/* Columns Visibility */}
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                columnsPopoverState.handleClick(e);
                closeMenu();
              }}
            >
              <ListItemIcon>
                <ColumnsIcon />
              </ListItemIcon>
              <ListItemText>{t('COMMON.COLUMNS')}</ListItemText>
            </MenuItem>
          </Menu>

          {/* ColumnsVisibility moved outside Menu to stay visible */}
          <ColumnsVisibility
            popoverState={columnsPopoverState}
            visibleColumns={tableInstanceRef?.current?.allColumns ?? []}
            onResetColumns={resetColumns}
          />

          <RowSizePickerPopover
            onChange={onRowSizeChange}
            open={rowSizePickerOpen}
            setOpen={setRowSizePickerOpen}
          />
        </Box>
      ) : (
        <Box display="flex" alignItems="center">
          <CheckboxRadioItem
            checked={automaticNumbering}
            onChange={(e) => {
              onAutomaticNumberingChange(e.target.checked);
            }}
            label={t('CHECK_LIST_POSITIONS_TABLE.AUTOMATIC_NUMBERS')}
            horizontalPadding
            verticalPadding
          />

          <RowSizePicker value={rowSize} onChange={onRowSizeChange} />

          <CheckListPositionsTableHeaderColumnsVisibility
            tableInstanceRef={tableInstanceRef}
          />
        </Box>
      )}
    </Box>
  );
};
